import { pickPreferredDevice } from '@open-songster/core'
import type { SpotifyTrackInput } from '@open-songster/shared'
import { isRecord } from '../lib/storage'
import { spotifyAuthClient } from './spotify-auth'

const API = 'https://api.spotify.com/v1'

export type SpotifyErrorKind =
  | 'premium_required'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'no_active_device'
  | 'device_unavailable'
  | 'unauthorized'
  | 'unknown'

export class SpotifyError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly kind: SpotifyErrorKind,
    readonly retryAfterSeconds?: number,
  ) {
    super(message)
    this.name = 'SpotifyError'
  }
}

function classify(status: number, reason: string | null): SpotifyErrorKind {
  if (reason === 'PREMIUM_REQUIRED') return 'premium_required'
  if (reason === 'NO_ACTIVE_DEVICE') return 'no_active_device'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 429) return 'rate_limited'
  return 'unknown'
}

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await spotifyAuthClient.getAccessToken()
  const response = await fetch(path.startsWith('http') ? path : `${API}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
  })
  if (response.ok) return response

  const body: unknown = await response.json().catch(() => null)
  const error = isRecord(body) && isRecord(body.error) ? body.error : null
  const reason = error && typeof error.reason === 'string' ? error.reason : null
  const message =
    error && typeof error.message === 'string'
      ? error.message
      : `Spotify responded ${response.status}`
  const retryAfter = Number(response.headers.get('retry-after'))
  throw new SpotifyError(
    message,
    response.status,
    classify(response.status, reason),
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  )
}

async function getJson<T>(path: string): Promise<T> {
  const response = await call(path)
  return (await response.json()) as T
}

export interface SpotifyProfile {
  id: string
  displayName: string | null
  /** `premium` | `free` | `open` — playback needs `premium`. */
  product: string | null
}

export interface SpotifyDevice {
  id: string | null
  name: string
  type: string
  isActive: boolean
  isRestricted: boolean
}

export interface SpotifyPlaylistMeta {
  id: string
  name: string
  ownerName: string | null
  coverUrl: string | null
  trackCount: number
}

interface RawImage {
  url?: unknown
}
interface RawPlaylist {
  id?: unknown
  name?: unknown
  owner?: { display_name?: unknown }
  images?: RawImage[]
  tracks?: { total?: unknown }
}

export async function getProfile(): Promise<SpotifyProfile> {
  const raw = await getJson<{ id?: unknown; display_name?: unknown; product?: unknown }>('/me')
  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    displayName: typeof raw.display_name === 'string' ? raw.display_name : null,
    product: typeof raw.product === 'string' ? raw.product : null,
  }
}

export function isPremium(profile: SpotifyProfile): boolean {
  return profile.product === 'premium'
}

export async function getPlaylistMeta(playlistId: string): Promise<SpotifyPlaylistMeta> {
  const raw = await getJson<RawPlaylist>(
    `/playlists/${playlistId}?fields=id,name,owner(display_name),images,tracks(total)`,
  )
  return {
    id: typeof raw.id === 'string' ? raw.id : playlistId,
    name: typeof raw.name === 'string' ? raw.name : playlistId,
    ownerName: typeof raw.owner?.display_name === 'string' ? raw.owner.display_name : null,
    coverUrl: firstImageUrl(raw.images),
    trackCount: typeof raw.tracks?.total === 'number' ? raw.tracks.total : 0,
  }
}

function firstImageUrl(images: RawImage[] | undefined): string | null {
  const url = images?.[0]?.url
  return typeof url === 'string' ? url : null
}

interface RawPlaylistItem {
  /** February 2026: the track moved from `track` to `item`. */
  item?: RawTrack
  /** Deprecated by Spotify; still read so an older response shape works. */
  track?: RawTrack
  /** Local-file flag moved from the track onto the wrapper. */
  is_local?: unknown
}

interface RawTrack {
  id?: unknown
  uri?: unknown
  name?: unknown
  duration_ms?: unknown
  is_local?: unknown
  type?: unknown
  artists?: Array<{ id?: unknown; name?: unknown }>
  external_ids?: { isrc?: unknown }
  album?: { name?: unknown; album_type?: unknown; release_date?: unknown; images?: RawImage[] }
}

/**
 * Spotify's February 2026 changes renamed the playlist item endpoints from
 * `/tracks` to `/items` (GET, POST, PUT and DELETE alike). The old path returns
 * 403 Forbidden, which is indistinguishable from the dev-mode "you do not own
 * this playlist" error — so do not change this back.
 */

/** Spotify pages playlist items; real playlists exceed a single page. */
const PAGE_SIZE = 100

interface TrackPage {
  items?: RawPlaylistItem[]
  total?: unknown
  next?: unknown
}

/**
 * Reads every track of a playlist. Local files, podcast episodes and removed
 * tracks are skipped — they have no Spotify ID and cannot be played or dated.
 */
export async function getPlaylistTracks(
  playlistId: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<SpotifyTrackInput[]> {
  const fields =
    'items(is_local,item(id,uri,name,duration_ms,type,artists(id,name),external_ids(isrc),album(name,album_type,release_date,images))),total,next'
  let url: string | null =
    `${API}/playlists/${playlistId}/items?limit=${PAGE_SIZE}&offset=0&fields=${encodeURIComponent(fields)}`
  const tracks: SpotifyTrackInput[] = []
  const artistIds = new Map<string, string[]>()
  let total = 0

  while (url !== null) {
    const page: TrackPage = await getJson<TrackPage>(url)
    if (typeof page.total === 'number') total = page.total
    for (const item of page.items ?? []) {
      const read = toTrackInput(item)
      if (!read) continue
      tracks.push(read.track)
      artistIds.set(read.track.id, read.artistIds)
    }
    onProgress?.(tracks.length, total)
    url = typeof page.next === 'string' ? page.next : null
  }
  return withGenres(tracks, artistIds)
}

/**
 * Spotify has no per-track genre, so a track is filed under its artists'
 * genres. Best effort: the genres endpoint has been withdrawn from dev-mode
 * apps before, and a 403 or a rate limit there must not fail the import.
 */
async function withGenres(
  tracks: SpotifyTrackInput[],
  artistIdsByTrack: Map<string, string[]>,
): Promise<SpotifyTrackInput[]> {
  const allArtistIds = [...new Set([...artistIdsByTrack.values()].flat())]
  if (allArtistIds.length === 0) return tracks
  let genresByArtist: Map<string, string[]>
  try {
    genresByArtist = await getArtistGenres(allArtistIds)
  } catch (caught) {
    console.warn('Could not read artist genres; importing without them', caught)
    return tracks
  }
  return tracks.map((track) => {
    const genres = [
      ...new Set(
        (artistIdsByTrack.get(track.id) ?? []).flatMap((id) => genresByArtist.get(id) ?? []),
      ),
    ]
    return genres.length > 0 ? { ...track, genres } : track
  })
}

/** `GET /artists` accepts at most this many IDs per call. */
const ARTIST_BATCH = 50

interface RawArtistsPage {
  artists?: Array<{ id?: unknown; genres?: unknown } | null>
}

/** Genres per artist ID, in the order Spotify lists them (most relevant first). */
export async function getArtistGenres(
  artistIds: readonly string[],
): Promise<Map<string, string[]>> {
  const genres = new Map<string, string[]>()
  const unique = [...new Set(artistIds)]
  for (let offset = 0; offset < unique.length; offset += ARTIST_BATCH) {
    const batch = unique.slice(offset, offset + ARTIST_BATCH)
    const page = await getJson<RawArtistsPage>(
      `/artists?ids=${encodeURIComponent(batch.join(','))}`,
    )
    for (const artist of page.artists ?? []) {
      if (!artist || typeof artist.id !== 'string') continue
      const list = Array.isArray(artist.genres)
        ? artist.genres.filter((g): g is string => typeof g === 'string' && g.length > 0)
        : []
      genres.set(artist.id, list)
    }
  }
  return genres
}

function nonEmptyStrings(values: readonly unknown[]): string[] {
  return values.filter((value): value is string => typeof value === 'string' && value.length > 0)
}

interface ReadTrack {
  track: SpotifyTrackInput
  artistIds: string[]
}

function toTrackInput(entry: RawPlaylistItem): ReadTrack | null {
  const track = entry.item ?? entry.track
  if (!track) return null
  if (entry.is_local === true || track.is_local === true) return null
  if (typeof track.type === 'string' && track.type !== 'track') return null
  if (typeof track.id !== 'string' || typeof track.uri !== 'string') return null
  const title = typeof track.name === 'string' ? track.name : ''
  if (title.length === 0) return null

  const artists = nonEmptyStrings((track.artists ?? []).map((artist) => artist.name))
  const artistIds = nonEmptyStrings((track.artists ?? []).map((artist) => artist.id))

  const isrc = track.external_ids?.isrc
  const releaseDate = track.album?.release_date
  const albumName = track.album?.name
  const albumType = track.album?.album_type
  const coverUrl = firstImageUrl(track.album?.images)

  return {
    track: {
      id: track.id,
      uri: track.uri,
      title,
      artists,
      ...(typeof isrc === 'string' && isrc.length > 0 ? { isrc } : {}),
      ...(typeof releaseDate === 'string' ? { albumReleaseDate: releaseDate } : {}),
      ...(typeof albumName === 'string' ? { albumName } : {}),
      ...(typeof albumType === 'string' ? { albumType } : {}),
      ...(coverUrl ? { coverUrl } : {}),
      ...(typeof track.duration_ms === 'number' ? { durationMs: track.duration_ms } : {}),
    },
    artistIds,
  }
}

/**
 * One track by ID, for adding a single song to a playlist. Same shape and the
 * same best-effort genre lookup as a playlist read. Null when Spotify has no
 * playable track under that ID (a podcast episode, a local file, a typo).
 */
export async function getTrack(trackId: string): Promise<SpotifyTrackInput | null> {
  const raw = await getJson<RawTrack>(`/tracks/${encodeURIComponent(trackId)}`)
  const read = toTrackInput({ item: raw })
  if (!read) return null
  const [track] = await withGenres([read.track], new Map([[read.track.id, read.artistIds]]))
  return track ?? null
}

export async function getDevices(): Promise<SpotifyDevice[]> {
  const raw = await getJson<{
    devices?: Array<{
      id?: unknown
      name?: unknown
      type?: unknown
      is_active?: unknown
      is_restricted?: unknown
    }>
  }>('/me/player/devices')
  return (raw.devices ?? []).map((device) => ({
    id: typeof device.id === 'string' ? device.id : null,
    name: typeof device.name === 'string' ? device.name : 'Unknown device',
    type: typeof device.type === 'string' ? device.type : 'Unknown',
    isActive: device.is_active === true,
    isRestricted: device.is_restricted === true,
  }))
}

export async function transferPlayback(deviceId: string, play = false): Promise<void> {
  await call('/me/player', {
    method: 'PUT',
    body: JSON.stringify({ device_ids: [deviceId], play }),
  })
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** How long a Connect speaker gets to wake up after a transfer (8 × 500 ms). */
const TRANSFER_POLL_MS = 500
const TRANSFER_POLL_ATTEMPTS = 8
const PLAY_RETRY_DELAY_MS = 800

async function findDevice(deviceId: string): Promise<SpotifyDevice | null> {
  const devices = await getDevices()
  return devices.find((device) => device.id === deviceId) ?? null
}

/**
 * Makes `deviceId` Spotify's active device before we send it a track.
 *
 * Sending `play?device_id=` straight to an inactive device is unreliable:
 * speakers such as Sonos acknowledge with 202 and then do nothing, and a
 * freshly registered Web Playback SDK device 404s. Transferring first, then
 * waiting until Spotify reports the device active, is what the official
 * clients do.
 */
export async function ensureActiveDevice(deviceId: string): Promise<void> {
  // A device that connected a moment ago (our own SDK player) can take a
  // second to show up in the list, so one miss is not yet "gone".
  let target = await findDevice(deviceId)
  if (!target) {
    await sleep(TRANSFER_POLL_MS * 2)
    target = await findDevice(deviceId)
  }
  if (!target) {
    throw new SpotifyError('The selected device is not available', 404, 'device_unavailable')
  }
  if (target.isActive) return

  await transferPlayback(deviceId, false)
  for (let attempt = 0; attempt < TRANSFER_POLL_ATTEMPTS; attempt++) {
    await sleep(TRANSFER_POLL_MS)
    const device = await findDevice(deviceId)
    if (device?.isActive) return
  }
  // Some devices never flip `is_active` until they actually play; let the
  // play call have its chance rather than giving up here.
}

function isTransientPlayFailure(error: unknown): boolean {
  if (!(error instanceof SpotifyError)) return false
  return (
    error.kind === 'not_found' ||
    error.kind === 'no_active_device' ||
    error.status === 502 ||
    error.status === 503
  )
}

/**
 * The device Spotify is currently playing on, from the playback-state
 * endpoint. It can know about a device that `/me/player/devices` omits, and
 * answers 204 with no body when there is no active player at all.
 */
export async function getActiveDeviceId(): Promise<string | null> {
  const response = await call('/me/player')
  if (response.status === 204) return null
  const body: unknown = await response.json().catch(() => null)
  if (!isRecord(body) || !isRecord(body.device)) return null
  return typeof body.device.id === 'string' ? body.device.id : null
}

async function sendPlay(deviceId: string | null, trackUri: string): Promise<void> {
  const query = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ''
  await call(`/me/player/play${query}`, {
    method: 'PUT',
    body: JSON.stringify({ uris: [trackUri], position_ms: 0 }),
  })
}

/**
 * Starts `trackUri` from the top on `deviceId`, activating the device first
 * and retrying once on the transient errors Spotify Connect is known for.
 *
 * With `deviceId === null` ("automatic") the command goes to whatever device
 * is active in the Spotify app. If there is none, the best device from the
 * list is activated instead; if the list is empty too, the caller gets a
 * `no_active_device` error to explain.
 */
export async function playTrack(deviceId: string | null, trackUri: string): Promise<void> {
  if (deviceId) await ensureActiveDevice(deviceId)
  try {
    await sendPlay(deviceId, trackUri)
  } catch (error) {
    if (!isTransientPlayFailure(error)) throw error
    if (deviceId === null) {
      const active = await getActiveDeviceId().catch(() => null)
      if (active) {
        await sendPlay(active, trackUri)
        return
      }
      const fallback = pickPreferredDevice(await getDevices())
      if (!fallback?.id) {
        throw new SpotifyError('Spotify has no active device', 404, 'no_active_device')
      }
      await ensureActiveDevice(fallback.id)
      await sendPlay(fallback.id, trackUri)
      return
    }
    await sleep(PLAY_RETRY_DELAY_MS)
    await sendPlay(deviceId, trackUri)
  }
}

export async function resumePlayback(deviceId: string | null): Promise<void> {
  const query = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ''
  await call(`/me/player/play${query}`, { method: 'PUT' })
}

export async function pausePlayback(deviceId: string | null): Promise<void> {
  const query = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ''
  await call(`/me/player/pause${query}`, { method: 'PUT' })
}

/**
 * Repeat mode is per playback session, not per track, so once set it survives
 * every following `play` command until reset.
 */
export async function setRepeatMode(
  deviceId: string | null,
  state: 'track' | 'context' | 'off',
): Promise<void> {
  const query = deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ''
  await call(`/me/player/repeat?state=${state}${query}`, { method: 'PUT' })
}

/**
 * Spotify has no "stop": pausing and seeking back to the start is the closest
 * equivalent, and leaves the next scan a clean slate.
 */
export async function stopPlayback(deviceId: string | null): Promise<void> {
  const query = deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ''
  await pausePlayback(deviceId)
  await call(`/me/player/seek?position_ms=0${query}`, { method: 'PUT' }).catch(() => {
    // Seeking is cosmetic; a paused track is already "stopped" for our purposes.
  })
}
